import { INITIAL_SERVICE_PROFESSIONALS_PARAM, serializeInitialServiceProfessionals, type InitialServiceProfessional } from "@/app/booking/core/appointmentInitialServiceProfessionals";
export const LOCATION_CONFIGURE_SERVICE_PARAM = "configure_service_id";
export const LOCATION_CONFIGURE_VARIANT_PARAM = "configure_variant_id";
export const LOCATION_INITIAL_PROFESSIONAL_PARAM = "professional";
export const LOCATION_PROFILE_MODAL_PARAM = "modal";
export const LOCATION_PROFILE_EMPLOYEE_PARAM = "employee";
export const LOCATION_PROFILE_EMPLOYEE_MODAL = "employee-profile";

export const LOCATION_PROFILE_PORTFOLIO_PARAM = "portfolio";

export type LocationPhotoCollection =
  | "venue"
  | "portfolio"
  | "services"
  | "team-portfolio";

export function buildLocationPhotoHref(
  locationId: string,
  collection: LocationPhotoCollection,
  imageId: string,
) {
  const image = normalizeIdentifier(imageId);
  if (!image) throw new Error("Location photo identity cannot be empty.");
  const params = new URLSearchParams({ modal: "gallery", collection, image });
  return `${buildLocationProfilePath(locationId)}?${params.toString()}`;
}

/** Exact presentation intent; only the location's current media can resolve it. */
export function readLocationPhotoIntent(
  params: Pick<URLSearchParams, "getAll" | "toString">,
): { collection: LocationPhotoCollection; imageId: string } | null {
  const modal = params.getAll("modal");
  const collection = params.getAll("collection");
  const image = params.getAll("image");
  if (
    modal.length !== 1 || modal[0] !== "gallery" ||
    collection.length !== 1 || image.length !== 1
  ) return null;
  const collectionId = collection[0];
  if (
    collectionId !== "venue" && collectionId !== "portfolio" &&
    collectionId !== "services" && collectionId !== "team-portfolio"
  ) return null;
  const imageId = normalizeIdentifier(image[0]);
  if (!imageId || imageId !== image[0]) return null;
  const canonical = new URLSearchParams({
    modal: "gallery",
    collection: collectionId,
    image: imageId,
  });
  return params.toString() === canonical.toString()
    ? { collection: collectionId, imageId }
    : null;
}

export type LocationCatalogView = "services" | "packages";

export interface LocationServicesHrefOptions {
  appointmentMode?: "individual" | "group";
  catalog?: LocationCatalogView;
  configureServiceId?: string | null;
  configureVariantId?: string | null;
  initialProfessionalId?: string | null;
  initialServiceProfessionals?: readonly InitialServiceProfessional[];
  focusServiceId?: string | null;
  focusPackageId?: string | null;
  packageIds?: readonly string[];
  serviceIds?: readonly string[];
  serviceVariants?: string | null;
  serviceAddOns?: string | null;
  serviceBundleAddOns?: string | null;
}

function encodeRouteSegment(value: string) {
  const normalized = value.trim();

  if (!normalized) {
    throw new Error("Location route segments cannot be empty.");
  }

  return encodeURIComponent(normalized);
}

function normalizeIdentifier(value: string | null | undefined) {
  const normalized = value?.trim() ?? "";
  return normalized && normalized.length <= 160 ? normalized : null;
}

function normalizeIdentifierList(values: readonly string[] | undefined) {
  const normalized = values
    ?.map(normalizeIdentifier)
    .filter((value): value is string => value !== null);

  return Array.from(new Set(normalized ?? []));
}

function normalizeSingleIdentifierList(values: readonly string[] | undefined) {
  if (values?.length !== 1) {
    return [];
  }

  const normalized = normalizeIdentifier(values[0]);
  return normalized ? [normalized] : [];
}

function normalizeSerializedConfiguration(value: string | null | undefined) {
  const normalized = value?.trim() ?? "";
  return normalized && normalized.length <= 8_000 ? normalized : null;
}

export function buildLocationProfilePath(locationId: string) {
  return `/locations/${encodeRouteSegment(locationId)}`;
}

/** Canonical query-free chooser for an unscoped Location booking action. */
export function buildLocationAllOfferPath(locationId: string) {
  return `${buildLocationProfilePath(locationId)}/all-offer`;
}

/** Gift-type selection only; monetary Gift Cards retain their own routes. */
export function buildLocationGiftsPath(locationId: string) {
  return `${buildLocationProfilePath(locationId)}/gifts`;
}

export function buildLocationEmployeeProfileHref(
  locationId: string,
  userLocationId: string,
  portfolioImageId?: string | null,
) {
  const employeeId = normalizeIdentifier(userLocationId);
  if (!employeeId) {
    throw new Error("Location employee profile identity cannot be empty.");
  }

  const params = new URLSearchParams({
    [LOCATION_PROFILE_MODAL_PARAM]: LOCATION_PROFILE_EMPLOYEE_MODAL,
    [LOCATION_PROFILE_EMPLOYEE_PARAM]: employeeId,
  });
  const imageId = normalizeIdentifier(portfolioImageId);
  if (imageId) params.set(LOCATION_PROFILE_PORTFOLIO_PARAM, imageId);
  return `${buildLocationProfilePath(locationId)}?${params.toString()}`;
}

/**
 * Reads the exact URL-owned employee dialog intent. Repeated, unknown, blank,
 * or oversized values fail closed instead of opening a stale team profile.
 */
export function readLocationEmployeeProfileIntent(
  searchParams: Pick<URLSearchParams, "getAll" | "toString">,
) {
  const modalValues = searchParams.getAll(LOCATION_PROFILE_MODAL_PARAM);
  const employeeValues = searchParams.getAll(LOCATION_PROFILE_EMPLOYEE_PARAM);
  if (
    modalValues.length !== 1 ||
    modalValues[0] !== LOCATION_PROFILE_EMPLOYEE_MODAL ||
    employeeValues.length !== 1
  ) {
    return null;
  }

  const employeeId = normalizeIdentifier(employeeValues[0]);
  if (!employeeId || employeeId !== employeeValues[0]) {
    return null;
  }

  const canonicalParams = new URLSearchParams({
    [LOCATION_PROFILE_MODAL_PARAM]: LOCATION_PROFILE_EMPLOYEE_MODAL,
    [LOCATION_PROFILE_EMPLOYEE_PARAM]: employeeId,
  });

  const imageValues = searchParams.getAll(LOCATION_PROFILE_PORTFOLIO_PARAM);
  if (imageValues.length > 1) return null;
  if (imageValues.length === 1) {
    const imageId = normalizeIdentifier(imageValues[0]);
    if (!imageId || imageId !== imageValues[0]) return null;
    canonicalParams.set(LOCATION_PROFILE_PORTFOLIO_PARAM, imageId);
  }

  return searchParams.toString() === canonicalParams.toString()
    ? employeeId
    : null;
}

export function buildLocationServicesPath(locationId: string) {
  return `${buildLocationProfilePath(locationId)}/services`;
}

/**
 * Builds the canonical Location-owned Step-1 URL. It mirrors the established
 * Specialist selector query contract while keeping the route identity owned
 * by the exact location instead of a staff assignment.
 */
export function buildLocationServicesHref(
  locationId: string,
  options: LocationServicesHrefOptions = {},
) {
  const params = new URLSearchParams();
  const packageIds = normalizeSingleIdentifierList(options.packageIds);
  const serviceIds = normalizeIdentifierList(options.serviceIds);
  const serviceVariants = normalizeSerializedConfiguration(
    options.serviceVariants,
  );
  const serviceAddOns = normalizeSerializedConfiguration(options.serviceAddOns);
  const serviceBundleAddOns = normalizeSerializedConfiguration(
    options.serviceBundleAddOns,
  );
  const configureServiceId = normalizeIdentifier(options.configureServiceId);
  const configureVariantId = normalizeIdentifier(options.configureVariantId);
  const initialProfessionalId = normalizeIdentifier(
    options.initialProfessionalId,
  );
  const focusServiceId = normalizeIdentifier(options.focusServiceId);
  const focusPackageId = normalizeIdentifier(options.focusPackageId);

  if (options.appointmentMode === "group") {
    params.set("appointment_mode", "group");
  }

  if (options.catalog === "packages") {
    params.set("catalog", "packages");
  }

  const serviceProfessionals = serializeInitialServiceProfessionals(options.initialServiceProfessionals?.filter(row => serviceIds.includes(row.serviceId)));
  if (options.catalog !== "packages" && options.appointmentMode !== "group" && !initialProfessionalId && serviceProfessionals) {
    params.set(INITIAL_SERVICE_PROFESSIONALS_PARAM, serviceProfessionals);
  }

  if (options.catalog !== "packages" && initialProfessionalId) {
    params.set(LOCATION_INITIAL_PROFESSIONAL_PARAM, initialProfessionalId);
  }

  if (serviceIds.length > 0) {
    params.set("services", serviceIds.join(","));
  }

  if (serviceVariants) {
    params.set("service_variants", serviceVariants);
  }

  if (serviceAddOns) {
    params.set("service_addons", serviceAddOns);
  }

  if (serviceBundleAddOns) {
    params.set("service_bundle_addons", serviceBundleAddOns);
  }

  if (packageIds.length > 0) {
    params.set("packages", packageIds[0]!);
  }

  const canEmitServiceConfigurationIntent =
    options.catalog !== "packages" &&
    configureServiceId !== null &&
    !serviceIds.includes(configureServiceId);

  if (canEmitServiceConfigurationIntent) {
    params.set(LOCATION_CONFIGURE_SERVICE_PARAM, configureServiceId);

    if (configureVariantId) {
      params.set(LOCATION_CONFIGURE_VARIANT_PARAM, configureVariantId);
    }
  }

  if (
    !canEmitServiceConfigurationIntent &&
    options.catalog === "packages" &&
    focusPackageId &&
    packageIds.includes(focusPackageId)
  ) {
    params.set("focus_package_id", focusPackageId);
  } else if (
    !canEmitServiceConfigurationIntent &&
    options.catalog !== "packages" &&
    focusServiceId &&
    serviceIds.includes(focusServiceId)
  ) {
    params.set("focus_service_id", focusServiceId);
  }

  const search = params.toString();
  return `${buildLocationServicesPath(locationId)}${search ? `?${search}` : ""}`;
}
